import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import whiteSVG from "../assets/images/WhiteBackground.png"

const PrivacyPolicy = () => {
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640)
    }
    checkMobile()
    window.addEventListener("resize", checkMobile)
    return () => window.removeEventListener("resize", checkMobile)
  }, [])

  return (
    <div className="bg-white">
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
        style={{
          backgroundImage: `url(${whiteSVG})`,
          backgroundSize: isMobile ? "cover" : "100% 100%",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          padding: "10px",
          marginTop: "-100px",
          paddingTop: "200px",
          paddingLeft: isMobile ? "20px" : "220px",
          paddingRight: isMobile ? "20px" : "300px",
          paddingBottom: "200px",
        }}
        className="font-bold text-4xl sm:text-5xl text-center sm:text-left mb-12"
      >
        Privacy Policy
      </motion.div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          className="bg-white shadow-lg rounded-2xl p-6 sm:p-8"
        >
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6 text-center">
            Privacy Policy for Lightning Technologies
          </h2>

          {/* Plain-language summary */}
          <section className="mb-6">
            <p className="text-gray-700 leading-relaxed text-sm sm:text-base text-justify break-words">
              <span className="font-semibold">Effective date:</span> 18 October 2023
            </p>
            <p className="text-gray-700 leading-relaxed text-sm sm:text-base text-justify break-words">
              Lightning Technologies (the company behind MarketMind) respects your privacy. We collect only the
              information we need to provide and secure your MarketMind account — things like your name, company name,
              email, phone, address, and government ID (if required for business verification). We store your data on
              secure cloud infrastructure and do <span className="font-semibold">not</span> use cookies, trackers, or sell
              your data. You control your data — you can access, correct, request portability, or request deletion by
              contacting <a href="mailto:privacy@lightningtechnologies.net" className="text-black font-semibold">privacy@lightningtechnologies.net</a>.
              Full details below.
            </p>
          </section>

          {/* Full policy */}
          <section className="space-y-4 sm:space-y-6">
            <div>
              <h4 className="text-lg font-semibold mt-3">1. Introduction — what this policy does and who it applies to</h4>
              <p className="text-gray-700 leading-relaxed text-sm sm:text-base text-justify break-words">
                This Privacy Policy explains in clear detail how Lightning Technologies (“we”, “us”, “our”) collects,
                uses, stores, protects, and shares personal information when you use <span className="font-semibold">MarketMind</span>, our web-based ERP
                platform, or otherwise interact with our website or support. It applies to all users of MarketMind
                (commission agents, firms, supply-chain stakeholders) and to visitors of our website.
              </p>
              <p className="text-gray-700 leading-relaxed text-sm sm:text-base text-justify break-words">
                <span className="font-semibold">Why this exists:</span> we want you to be fully informed about the data we
                handle and to make it easy for you to control your information.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mt-3">2. What information we collect</h4>
              <p className="text-gray-700 leading-relaxed text-sm sm:text-base text-justify break-words">
                We collect a limited set of personal and technical information that helps us create and secure your
                account and deliver the service.
              </p>

              <h5 className="font-semibold mt-2">Personal information</h5>
              <ul className="list-disc ml-5 text-gray-700">
                <li><span className="font-semibold">Name</span> — so we can address you and verify identity.</li>
                <li><span className="font-semibold">Company / Firm name</span> — to set up your business account and access levels.</li>
                <li><span className="font-semibold">Email address</span> — used for account creation, login, notifications, and support.</li>
                <li><span className="font-semibold">Phone number</span> — for account verification and urgent support (if you opt to provide it).</li>
                <li><span className="font-semibold">Address</span> — business or billing address when required.</li>
                <li><span className="font-semibold">Government ID</span> (when required by you or for business verification) — e.g., GSTIN or other business registration identifiers.</li>
              </ul>
              <p className="text-gray-700 mt-2">
                <span className="font-semibold">Important:</span> When a commission agent signs up, they provide name, firm name, email and phone — that lets us create the firm account and give the appropriate access.
              </p>

              <h5 className="font-semibold mt-3">Technical & usage information</h5>
              <ul className="list-disc ml-5 text-gray-700">
                <li><span className="font-semibold">Device information</span> — basic device type and browser (helps troubleshoot).</li>
                <li><span className="font-semibold">IP address</span> — for security logging and to detect suspicious activity.</li>
                <li><span className="font-semibold">Account/usage logs</span> — timestamps of sign-in, actions taken inside MarketMind (for support and security).</li>
              </ul>
              <p className="text-gray-700 mt-2">
                <span className="font-semibold">Important:</span> we do <span className="font-semibold">not</span> use cookies, tracking pixels, or analytics tools for behavior tracking or advertising. (If this ever changes, we will update this policy and tell you.)
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mt-3">3. How we collect information</h4>
              <ul className="list-disc ml-5 text-gray-700">
                <li><span className="font-semibold">Directly from you:</span> when you fill registration forms, submit documents, or contact support.</li>
                <li><span className="font-semibold">From your use of the web app:</span> automated logs such as IP and activity logs are collected by our application for security and troubleshooting.</li>
                <li><span className="font-semibold">Not from third-party tracking:</span> we do not deploy advertising trackers, analytics platforms, or social sign-in that would import extra data about you.</li>
              </ul>
              <p className="text-gray-700 mt-2">
                <span className="font-semibold">Important:</span> when you register via MarketMind web form, the registration fields are the source of your personal info; the system also logs your IP and time of login for security.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mt-3">4. Why we collect it</h4>
              <p className="text-gray-700">We only collect data for specific, necessary purposes. Each purpose below includes an explanation and example.</p>
              <ol className="list-decimal ml-5 text-gray-700">
                <li className="mt-2">
                  <span className="font-semibold">Account creation & authentication</span>
                  <p className="mt-1">Why: to create and verify your MarketMind account and allow you to log in securely.</p>
                  <p className="mt-1">Important: your email and password let you sign in; phone may be used for two-factor verification.</p>
                </li>

                <li className="mt-3">
                  <span className="font-semibold">Provide and operate the service</span>
                  <p className="mt-1">Why: to perform the features of MarketMind (ledger, entries, stock management).</p>
                  <p className="mt-1">Important: company name and firm details are used to show the correct business dashboard and to separate your firm’s data from others.</p>
                </li>

                <li className="mt-3">
                  <span className="font-semibold">Security & fraud prevention</span>
                  <p className="mt-1">Why: to detect unauthorized access and secure all accounts.</p>
                  <p className="mt-1">Important: IP and login logs let us spot repeated failed logins and lock a compromised account.</p>
                </li>

                <li className="mt-3">
                  <span className="font-semibold">Support & troubleshooting</span>
                  <p className="mt-1">Why: to respond to your support requests and resolve technical issues.</p>
                  <p className="mt-1">Important: when you report a missing invoice, we use logs to reproduce and fix the issue.</p>
                </li>

                <li className="mt-3">
                  <span className="font-semibold">Legal & compliance obligations</span>
                  <p className="mt-1">Why: to meet legal, regulatory, or contractual obligations (for example, record-keeping required for business).</p>
                  <p className="mt-1">Important: if laws require retention of certain business records for audits, we retain the relevant data for that period.</p>
                </li>
              </ol>

              <p className="text-gray-700 mt-3">
                <span className="font-semibold">Note:</span> We do <span className="font-semibold">not</span> use your data for marketing or advertising purposes.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mt-3">5. Payments and billing</h4>
              <p className="text-gray-700">
                <span className="font-semibold">Current practice:</span> MarketMind accounts and subscriptions are managed directly between you and Lightning Technologies; we do not store card numbers or sensitive payment credentials on our systems. If an invoice or payment method is used, we will collect billing name and address to generate invoices.
              </p>
              <p className="text-gray-700 mt-2">
                <span className="font-semibold">If/when we use a payment gateway:</span> we will clearly notify you and update this policy. Sensitive payment details (card number, UPI ID) will <span className="font-semibold">not</span> be stored on our servers — they would be handled by the payment processor (if one is used).
              </p>
              <p className="text-gray-700 mt-2">
                If you need a copy of billing records please contact <a href="mailto:privacy@lightningtechnologies.net" className="text-black font-semibold">privacy@lightningtechnologies.net</a>.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mt-3">6. Sharing, third parties & service providers</h4>
              <ul className="list-disc ml-5 text-gray-700">
                <li><span className="font-semibold">We do not sell, rent, or trade your personal information.</span></li>
                <li><span className="font-semibold">Cloud storage:</span> to operate MarketMind we store customer data on cloud infrastructure providers such as AWS. These providers are processors who host data on our behalf and act according to our contractual instructions and security requirements.</li>
                <li><span className="font-semibold">No app-level third-party integrations:</span> we do not integrate your MarketMind data with third-party apps or analytics platforms. If that changes, we’ll update this policy and ask for your consent where required.</li>
              </ul>
              <p className="text-gray-700 mt-2">
                <span className="font-semibold">Why mention AWS:</span> although AWS is a third party, they operate under contract as our hosting provider. We do not expose your data to external services for advertising or profiling.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mt-3">7. Data storage & security — how we protect your data</h4>
              <p className="text-gray-700">
                We apply multiple, industry-standard safeguards:
              </p>
              <ul className="list-disc ml-5 text-gray-700">
                <li><span className="font-semibold">Encryption in transit and at rest:</span> communications between your browser and MarketMind are encrypted; stored data is encrypted on the servers.</li>
                <li><span className="font-semibold">Access controls & roles:</span> employees and administrators only have the access necessary to do their job; role-based permissions reduce exposure.</li>
                <li><span className="font-semibold">Multi-factor authentication (MFA):</span> for admin/back-end access we use MFA to further protect accounts.</li>
                <li><span className="font-semibold">Encrypted backups:</span> we keep encrypted backups to ensure disaster recovery.</li>
                <li><span className="font-semibold">Security testing & audits:</span> we perform periodic security checks and patching.</li>
                <li><span className="font-semibold">Employee training & least privilege:</span> staff with access to production systems follow strict policies and are trained on data protection.</li>
              </ul>
              <p className="text-gray-700 mt-2">
                <span className="font-semibold">Reality check:</span> no system is 100% immune to attacks — if a breach occurs we will follow our incident response process and notify affected users and authorities as required by law.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mt-3">8. Data retention — how long we keep your data</h4>
              <ul className="list-disc ml-5 text-gray-700">
                <li><span className="font-semibold">Active accounts:</span> we retain the data required to run your account for as long as you use MarketMind.</li>
                <li><span className="font-semibold">Deletion requests / account closure:</span> when you request deletion or close your account, we will deactivate the account and delete the primary data from active systems.</li>
              </ul>
              <p className="text-gray-700 mt-2">
                <span className="font-semibold">Typical timing:</span> primary account data will be removed from live systems within a reasonable period (we aim to remove active data promptly — e.g., within 30 days).
              </p>
              <p className="text-gray-700 mt-2">
                <span className="font-semibold">Backups & archives:</span> encrypted backups or audit logs may remain for a short additional period (commonly up to several weeks/months) before being purged, to ensure data integrity and recovery capability.
              </p>
              <p className="text-gray-700 mt-2">
                <span className="font-semibold">Legal hold:</span> if required by law (e.g., for tax or audit requirements) we may retain specific records for the legally required period.
              </p>
              <p className="text-gray-700 mt-2">
                If you want a specific retention timeline for a particular record, contact <a href="mailto:privacy@lightningtechnologies.net" className="text-black font-semibold">privacy@lightningtechnologies.net</a>.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mt-3">9. Your rights — a step-by-step guide</h4>
              <p className="text-gray-700">
                <span className="font-semibold">What you can ask us to do:</span>
              </p>
              <ul className="list-disc ml-5 text-gray-700">
                <li><span className="font-semibold">Access:</span> Request a copy of the personal information we hold about you.</li>
                <li><span className="font-semibold">Correction:</span> Ask us to correct inaccurate or incomplete information.</li>
                <li><span className="font-semibold">Deletion:</span> Request deletion of your personal information (subject to legal retention needs).</li>
                <li><span className="font-semibold">Portability:</span> Ask for a machine-readable copy of your data so you can move it elsewhere.</li>
                <li><span className="font-semibold">Withdraw consent:</span> For processing which we rely on consent for (if any), you may withdraw your consent.</li>
              </ul>
              <p className="text-gray-700 mt-2">
                <span className="font-semibold">How to request:</span> send an email to <a href="mailto:privacy@lightningtechnologies.net" className="text-black font-semibold">privacy@lightningtechnologies.net</a> with:
              </p>
              <ol className="list-decimal ml-5 text-gray-700">
                <li>Subject line: <em>Data request — [Access / Correction / Deletion / Portability]</em></li>
                <li>Your account email and firm name.</li>
                <li>Brief description of what you want. We may ask for reasonable identity verification to protect your account.</li>
              </ol>
              <p className="text-gray-700 mt-2">
                <span className="font-semibold">Response time:</span> we aim to acknowledge and fulfill requests promptly (typically we acknowledge within a few business days and complete the request within 30 days, except where legal or technical reasons require longer). If we cannot comply we will explain why.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mt-3">10. Cookies & tracking — short and explicit</h4>
              <p className="text-gray-700">
                We do not use cookies, tracking pixels, or analytics for advertising or user profiling.
              </p>
              <p className="text-gray-700 mt-2">
                The MarketMind web app does log basic technical info (IP, device type) for security and troubleshooting, but this is not used for behavioral advertising.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mt-3">11. Children & minors</h4>
              <p className="text-gray-700">
                MarketMind is <span className="font-semibold">not intended for individuals under 18</span>. We do not knowingly collect information from minors. If you believe a minor has given us information, please contact <a href="mailto:privacy@lightningtechnologies.net" className="text-black font-semibold">privacy@lightningtechnologies.net</a> so we can remove that data.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mt-3">12. Cross-border data transfers</h4>
              <p className="text-gray-700">
                Your data is hosted on cloud providers (such as AWS). These providers may store and process data in India and in other countries they operate in.
              </p>
              <p className="text-gray-700 mt-2">
                When data is transferred outside India, we ensure contractual and technical safeguards are in place to protect the data in transit and at rest.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mt-3">13. Data breach notification — what happens if something goes wrong</h4>
              <p className="text-gray-700">
                If we become aware of a security breach that affects your personal data we will:
              </p>
              <ol className="list-decimal ml-5 text-gray-700">
                <li>Contain and investigate the incident.</li>
                <li>Notify affected users and any required regulatory bodies as required by law.</li>
                <li>Provide information on mitigation steps you can take (for example, changing passwords).</li>
              </ol>
              <p className="text-gray-700 mt-2">
                We will act quickly and transparently, and will keep you updated about material developments.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mt-3">14. Policy updates & how you’ll be notified</h4>
              <p className="text-gray-700">
                We may update this Privacy Policy from time to time. The version on the website shows the effective date.
              </p>
              <p className="text-gray-700 mt-2">
                <span className="font-semibold">How you will be told:</span> we will communicate significant changes directly to active customers (for MarketMind customers we deploy updates or notify the primary account contact). Because MarketMind customers use isolated environments with dedicated databases, we typically communicate major changes during the update deployment process.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mt-3">15. Governing law & disputes</h4>
              <p className="text-gray-700">
                This Privacy Policy is governed by the laws applicable in <span className="font-semibold">Maharashtra, India</span>. Any disputes arising from this policy will be subject to the jurisdiction of courts in Maharashtra.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mt-3">16. Contact / Grievance Officer</h4>
              <p className="text-gray-700">
                If you have questions, want to exercise your rights, or have a complaint, contact:
              </p>
              <div className="mt-3">
                <p className="text-gray-700">
                  <span className="font-semibold">Privacy & Grievance Officer</span>
                  <br />
                  Email: <a href="mailto:privacy@lightningtechnologies.net" className="text-black font-semibold">privacy@lightningtechnologies.net</a>
                  <br />
                  Address: <span className="text-black font-semibold">Pune, Maharashtra, India</span>
                </p>
                <p className="text-gray-700 mt-2">
                  When you contact us, please include your account email and firm name to help us respond quickly.
                </p>
              </div>
            </div>

            {/* Short FAQ */}
            <div>
              <h3 className="text-xl sm:text-2xl font-semibold mt-6 mb-3">FAQ's</h3>
              <div className="space-y-3 text-gray-700">
                <div>
                  <p className="font-semibold">Q: What personal info do you collect?</p>
                  <p>A: Name, company name, email, phone, address, government ID (if required), plus technical details like IP and device info. We only collect what is necessary to provide MarketMind.</p>
                </div>

                <div>
                  <p className="font-semibold">Q: Do you use cookies or tracking?</p>
                  <p>A: No — we do not use cookies, pixels, or analytics tools for advertising or tracking.</p>
                </div>

                <div>
                  <p className="font-semibold">Q: Do you share my data with third parties?</p>
                  <p>A: We do not sell, rent, or share your data for advertising. We store customer data on cloud infrastructure providers (e.g., AWS) who act on our instructions.</p>
                </div>

                <div>
                  <p className="font-semibold">Q: How do I delete my account?</p>
                  <p>A: Email <a href="mailto:privacy@lightningtechnologies.net" className="text-black font-semibold">privacy@lightningtechnologies.net</a> with your request; we will verify identity and delete your account data from active systems; backups are purged per our retention schedule.</p>
                </div>

                <div>
                  <p className="font-semibold">Q: Is my data secure?</p>
                  <p>A: Yes — we use encryption, access controls, MFA for admin access, encrypted backups, and regular security practices to reduce risk.</p>
                </div>

                <div>
                  <p className="font-semibold">Q: Who can I contact with privacy concerns?</p>
                  <p>A: <a href="mailto:privacy@lightningtechnologies.net" className="text-black font-semibold">privacy@lightningtechnologies.net</a></p>
                </div>
              </div>
            </div>            
          </section>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="mt-8 sm:mt-12 p-4 sm:p-6 bg-[#BDE9FB] rounded-lg border border-gray-300 text-center"
          >
            <p className="text-xs sm:text-sm text-gray-700 italic">
              *We may update this Privacy Policy periodically to reflect changes in our practices, services, or legal
              requirements.*
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  )
}

export default PrivacyPolicy
